import { MapPin, Mail, Phone, Linkedin, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

function InstagramIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

const navLinks = [
  { label: 'Início',                  href: '/' },
  { label: 'Serviços',                href: '/#servicos' },
  { label: 'Sobre Nós',               href: '/sobre-nos' },
  { label: 'Blog',                    href: '/blog' },
  { label: 'Contato',                 href: '/contato' },
  { label: 'FAQ',                     href: '/faq' },
];

const offices = [
  {
    city: 'São Paulo',
    address: 'Av. Paulista, 2537 — Bela Vista',
    state: 'São Paulo — SP',
  },
  {
    city: 'Belo Horizonte',
    address: 'Rua Jornalista Djalma Andrade — Belvedere',
    state: 'Belo Horizonte — MG',
  },
];

const verticalLinesPattern = {
  backgroundImage:
    'repeating-linear-gradient(90deg, #D4512A 0, #D4512A 1px, transparent 1px, transparent 96px)',
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-10 flex items-center gap-3 mb-5">
      <span className="h-px w-6 bg-orange" />
      <h3 className="text-caption font-semibold uppercase tracking-[0.22em] text-neutral-300">
        {children}
      </h3>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="relative bg-neutral-950 text-neutral-400 overflow-hidden">
      {/* Pattern editorial sutil */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={verticalLinesPattern}
      />

      <div className="section-container relative py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          {/* Col 1 — Identidade (mais larga) */}
          <div className="md:col-span-4">
            <div className="h-10 flex items-center mb-5">
              <Link to="/" className="inline-flex items-center">
                <img src="/logo-on-dark.png" alt="Educa Legal" className="h-10 w-auto" />
              </Link>
            </div>
            <p className="text-body-sm leading-relaxed text-neutral-400 max-w-xs mb-7">
              O jurídico especializado no setor educacional. Atendimento 100% remoto em todo o
              Brasil.
            </p>

            {/* Redes sociais */}
            <div className="flex gap-2.5">
              <a
                href="https://instagram.com/educalegal"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 rounded-md border border-neutral-800 hover:border-orange hover:text-orange transition-colors"
                aria-label="Instagram"
              >
                <InstagramIcon size={16} />
              </a>
              <a
                href="https://linkedin.com/company/educalegal"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 rounded-md border border-neutral-800 hover:border-orange hover:text-orange transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={16} />
              </a>
            </div>
          </div>

          {/* Col 2 — Navegação */}
          <div className="md:col-span-3">
            <SectionLabel>Navegação</SectionLabel>
            <ul className="space-y-2.5">
              {navLinks.map((link) => {
                const isAnchor = link.href.includes('#');
                const className =
                  'group inline-flex items-baseline gap-1 text-body-sm text-neutral-400 hover:text-white transition-colors';
                return (
                  <li key={link.label}>
                    {isAnchor ? (
                      <a href={link.href} className={className}>
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.href} className={className}>
                        {link.label}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Col 3 — Contato */}
          <div className="md:col-span-2">
            <SectionLabel>Contato</SectionLabel>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://wa.me/5511989082892"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 text-body-sm text-neutral-400 hover:text-white transition-colors"
                >
                  <Phone size={13} className="flex-shrink-0 text-orange" />
                  <span className="tabular-nums">+55 11 98908-2892</span>
                  <ArrowUpRight
                    size={12}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </a>
              </li>
              <li>
                <a
                  href="mailto:contato@educalegal.com.br"
                  className="group inline-flex items-center gap-2 text-body-sm text-neutral-400 hover:text-white transition-colors"
                >
                  <Mail size={13} className="flex-shrink-0 text-orange" />
                  <span>contato@educalegal.com.br</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Col 4 — Escritórios */}
          <div className="md:col-span-3">
            <SectionLabel>Escritórios</SectionLabel>
            <ul className="space-y-4">
              {offices.map((office) => (
                <li key={office.city} className="flex gap-2.5">
                  <MapPin size={13} className="flex-shrink-0 mt-1 text-orange" />
                  <div className="leading-snug">
                    <div className="text-body-sm text-white font-medium">{office.city}</div>
                    <div className="text-caption text-neutral-500">{office.address}</div>
                    <div className="text-caption text-neutral-500">{office.state}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-neutral-800">
        <div className="section-container py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-caption text-neutral-500">
            © {new Date().getFullYear()} Educa Legal — Guimarães Neto Advogados. Todos os direitos
            reservados.
          </p>
          <p className="text-caption uppercase tracking-[0.18em] text-neutral-600">
            Direito Educacional · São Paulo · Belo Horizonte
          </p>
        </div>
      </div>
    </footer>
  );
}
